import { getSourceKey, type NumberedCitation } from "#features/citations/citedContent.ts";
import { getCitationSourceApp, type CitationSourceApp } from "#features/conversations/components/conversation-source/citationSource.tsx";
import { useHoveredSourceStore } from "#features/conversations/stores/useHoveredSourceStore.ts";
import { useSourcesSidePanelStore } from "#features/conversations/stores/useSourcesSidePanelStore.ts";
import { useCdnAssets } from "#hooks/useCdnAssets.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { useSidePanelStore } from "#stores/useSidePanelStore.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import type { ConversationMessage } from "#types/types.ts";
import { useColorSchemeContext } from "@hopper-ui/styled-system";
import { useCallback, useMemo } from "react";
import { Button } from "react-aria-components";
import { useShallow } from "zustand/shallow";
import "#css/ink.css";
import "./AssistantMessageToggleSourcesButton.css";

const MAX_APP_DISCS = 3;

// One disc per app: the app with the most sources first, and on a tie the app the answer cites
// first. `citations` is in display order, so the first one seen is the first cited.
const getChipApps = (citations: NumberedCitation[]): CitationSourceApp[] => {
    const groups = new Map<string, { app: CitationSourceApp; count: number; order: number }>();

    for (const { source } of citations) {
        const app = getCitationSourceApp(source);
        const group = groups.get(app.channel.nameKey);

        if (group) {
            group.count += 1;
        } else {
            groups.set(app.channel.nameKey, { app, count: 1, order: groups.size });
        }
    }

    return Array.from(groups.values())
        .sort((a, b) => b.count - a.count || a.order - b.order)
        .slice(0, MAX_APP_DISCS)
        .map(({ app }) => app);
};

interface Props {
    message: ConversationMessage;
    citations: NumberedCitation[];
}

/** The sources chip: the cited apps' marks in their colours and the count. It opens the sources modal. */
export const AssistantMessageToggleSourcesButton = ({ message, citations }: Props) => {
    const { tShared } = useSharedTranslation();
    const { trackEvent } = useTracking();
    const { getCdnAssetUrl } = useCdnAssets();
    const { colorScheme } = useColorSchemeContext();
    const apps = useMemo(() => getChipApps(citations), [citations]);
    const sourceKeys = useMemo(() => new Set(citations.map(({ source }) => getSourceKey(source))), [citations]);

    // The quiet echo: a citation pill of this answer is hovered (the pills set the hovered source).
    const isCitedSourceHovered = useHoveredSourceStore(state => state.hoveredSourceKey !== null && sourceKeys.has(state.hoveredSourceKey));

    const isSidePanelOpen = useSidePanelStore(state => state.isSidePanelOpen);
    const sidePanelType = useSidePanelStore(state => state.type);
    const isModal = useSidePanelStore(state => state.isModal);
    const [activeMessage, toggleSidePanel] = useSourcesSidePanelStore(useShallow(state => [state.activeMessage, state.toggleSidePanel]));

    const areSourcesVisible = isSidePanelOpen && sidePanelType === "sources" && activeMessage?.id === message.id;

    const onPress = useCallback(() => {
        trackEvent(MixpanelEvent.ButtonClicked, { Location: "Assistant Message", Trigger: "Toggle Message Sources List" });
        toggleSidePanel(message, citations);
    }, [citations, message, toggleSidePanel, trackEvent]);

    if (citations.length === 0) {
        return null;
    }

    return (
        <Button className="wai-sources-chip" onPress={onPress} aria-haspopup={isModal ? "dialog" : undefined} aria-expanded={areSourcesVisible} data-open={areSourcesVisible || undefined} data-source-hovered={isCitedSourceHovered || undefined}>
            <span className="wai-sources-chip-discs" aria-hidden="true">
                {apps.map(({ channel, brandMark, brandMarkDark, glyph }) => {
                    const schemeBrandMark = colorScheme === "dark" ? brandMarkDark : brandMark;

                    return (
                        <span key={channel.nameKey} className="wai-sources-chip-disc">
                            {schemeBrandMark ? <img src={getCdnAssetUrl(schemeBrandMark)} alt="" /> : glyph}
                        </span>
                    );
                })}
            </span>
            {tShared("sourcesCount", { count: citations.length })}
        </Button>
    );
};
